import { inventoryAPI } from './inventoryApi';

/**
 * Inventory Manager - Handles inventory operations for invoices
 * Ensures atomic operations and prevents duplicate deductions
 */

class InventoryManager {
  constructor() {
    this.processingInvoices = new Set(); // Track invoices being processed
  }

  /**
   * Process inventory changes when invoice status changes
   * @param {Object} invoice - Invoice object
   * @param {string} newStatus - New invoice status
   * @param {string} oldStatus - Previous invoice status
   */
  async processInvoiceStatusChange(invoice, newStatus, oldStatus = 'draft') {
    const invoiceId = invoice.id;
    
    // Prevent concurrent processing of the same invoice
    if (this.processingInvoices.has(invoiceId)) {
      console.log(`Invoice ${invoiceId} is already being processed`);
      return;
    }

    this.processingInvoices.add(invoiceId);

    try {
      const inventoryItems = invoice.items.filter(item => item.inventoryId);
      
      if (inventoryItems.length === 0) {
        console.log('No inventory items in invoice, skipping inventory processing');
        return;
      }

      // Check if inventory has already been processed for this invoice
      const isProcessed = await inventoryAPI.checkInventoryProcessed(invoiceId);
      
      switch (newStatus) {
        case 'finalized':
        case 'paid':
          await this.handleFinalization(invoice, inventoryItems, isProcessed);
          break;
          
        case 'cancelled':
        case 'returned':
          await this.handleCancellation(invoice, inventoryItems, isProcessed, oldStatus);
          break;
          
        case 'draft':
          // Moving back to draft - restore if it was previously finalized
          if (isProcessed && isProcessed.processed) {
            await this.handleDraftRestoration(invoice, inventoryItems);
          }
          break;
          
        default:
          console.log(`No inventory action needed for status: ${newStatus}`);
      }
    } catch (error) {
      console.error('Error processing inventory for invoice:', invoiceId, error);
      throw error;
    } finally {
      this.processingInvoices.delete(invoiceId);
    }
  }

  /**
   * Handle invoice finalization - deduct inventory
   */
  async handleFinalization(invoice, inventoryItems, isProcessed) {
    if (isProcessed && isProcessed.processed) {
      console.log(`Inventory already processed for invoice ${invoice.id}`);
      return;
    }

    // Validate stock availability before deduction
    const stockCheck = await inventoryAPI.checkStockAvailability(
      inventoryItems.map(item => ({
        id: item.inventoryId,
        quantity: item.quantity || item.qty
      }))
    );

    if (stockCheck.success === false) {
      throw new Error('Insufficient stock: ' + stockCheck.errors.join(', '));
    }

    // Deduct inventory
    await inventoryAPI.bulkUpdateQuantities(
      inventoryItems.map(item => ({
        id: item.inventoryId,
        quantity: item.quantity || item.qty
      })),
      invoice.id,
      'deduct'
    );

    // Mark as processed
    await inventoryAPI.markInventoryProcessed(invoice.id);

    console.log(`Inventory deducted for finalized invoice ${invoice.id}`);
  }

  /**
   * Handle invoice cancellation - restore inventory
   */
  async handleCancellation(invoice, inventoryItems, isProcessed, oldStatus) {
    // Only restore if the invoice was previously finalized/paid
    if (isProcessed && isProcessed.processed && 
        (oldStatus === 'finalized' || oldStatus === 'paid')) {
      
      await inventoryAPI.restoreStockForInvoice(
        inventoryItems,
        invoice.id,
        `Invoice cancelled from ${oldStatus} status`
      );

      // Unmark as processed
      await this.unmarkInventoryProcessed(invoice.id);

      console.log(`Inventory restored for cancelled invoice ${invoice.id}`);
    }
  }

  /**
   * Handle restoration when moving back to draft
   */
  async handleDraftRestoration(invoice, inventoryItems) {
    await inventoryAPI.restoreStockForInvoice(
      inventoryItems,
      invoice.id,
      'Invoice reverted to draft status'
    );

    // Unmark as processed
    await this.unmarkInventoryProcessed(invoice.id);

    console.log(`Inventory restored for draft invoice ${invoice.id}`);
  }

  /**
   * Unmark invoice as processed (if API supports it)
   */
  async unmarkInventoryProcessed(invoiceId) {
    try {
      // This would need to be implemented in the backend
      // For now, we'll just log it
      console.log(`Unmarking invoice ${invoiceId} as processed`);
    } catch (error) {
      console.error('Failed to unmark invoice as processed:', error);
    }
  }

  /**
   * Get inventory change log for audit
   */
  async getInventoryLog(filters = {}) {
    try {
      return await inventoryAPI.getInventoryLog(
        filters.productId,
        filters.startDate,
        filters.endDate
      );
    } catch (error) {
      console.error('Failed to get inventory log:', error);
      throw error;
    }
  }

  /**
   * Validate stock for multiple invoices (bulk operations)
   */
  async validateStockForInvoices(invoices) {
    const allInventoryItems = [];
    
    invoices.forEach(invoice => {
      const items = invoice.items.filter(item => item.inventoryId);
      allInventoryItems.push(...items);
    });

    if (allInventoryItems.length === 0) {
      return { valid: true, errors: [] };
    }

    try {
      const stockCheck = await inventoryAPI.checkStockAvailability(
        allInventoryItems.map(item => ({
          id: item.inventoryId,
          quantity: item.quantity || item.qty
        }))
      );

      return {
        valid: stockCheck.success !== false,
        errors: stockCheck.errors || []
      };
    } catch (error) {
      console.error('Failed to validate stock for invoices:', error);
      return { valid: false, errors: ['Stock validation failed'] };
    }
  }
}

// Export singleton instance
export const inventoryManager = new InventoryManager();
export default inventoryManager;
